import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Close as CloseIcon,
    GetApp as DownloadIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    RotateRight as RotateIcon,
    OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { 
    WorkerFile,
    previewWorkerFile,
    downloadWorkerFile
} from '../../api/workerFilesApi';

interface FilePreviewModalProps {
    file: WorkerFile;
    open: boolean;
    onClose: () => void;
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
    file,
    open,
    onClose
}) => {
    const [scale, setScale] = useState(1.0);
    const [rotation, setRotation] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string>('');
    
    React.useEffect(() => {
        if (open && file) {
            const loadPreview = async () => {
                try {
                    setLoading(true);
                    setError(null);
                    
                    const blob = await previewWorkerFile(file.id);
                    const url = window.URL.createObjectURL(blob);
                    setPreviewUrl(url);
                } catch (err: any) {
                    console.error('Failed to load preview:', err);
                    setError(err.response?.data?.message || 'Failed to load preview');
                } finally {
                    setLoading(false);
                }
            };
            
            loadPreview();
        }
        
        // Cleanup blob URL when modal closes or file changes
        return () => {
            if (previewUrl && previewUrl.startsWith('blob:')) {
                window.URL.revokeObjectURL(previewUrl);
            }
        };
    }, [open, file]);

    const handleDownload = async () => {
        try {
            const blob = await downloadWorkerFile(file.id);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.originalFileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error: any) {
            setError(`Failed to download file: ${error.message}`);
        }
    };

    const handleOpenInNewTab = () => {
        if (previewUrl) {
            window.open(previewUrl, '_blank');
        }
    };

    const zoomIn = () => {
        setScale(prevScale => Math.min(prevScale + 0.2, 3.0));
    };

    const zoomOut = () => {
        setScale(prevScale => Math.max(prevScale - 0.2, 0.5));
    };

    const rotate = () => {
        setRotation(prevRotation => (prevRotation + 90) % 360);
    };

    const isPDF = file.mimeType === 'application/pdf';
    const isImage = file.mimeType.startsWith('image/');

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6">{file.originalFileName}</Typography>
                        <Typography variant="caption" color="textSecondary">
                            {file.sizeBytes} bytes • {file.mimeType}
                        </Typography>
                    </Box>
                    <Box>
                        <IconButton onClick={handleDownload}>
                            <DownloadIcon />
                        </IconButton>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Box
                    sx={{
                        minHeight: 500,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {loading ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            gap={2}
                        >
                            <CircularProgress />
                            <Typography>Loading preview...</Typography>
                        </Box>
                    ) : error ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    ) : isPDF && previewUrl ? (
                        <Box
                            sx={{
                                width: '100%',
                                height: '70vh',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <iframe
                                src={previewUrl}
                                width="100%"
                                height="100%"
                                style={{
                                    border: 'none',
                                    transform: `scale(${scale}) rotate(${rotation}deg)`,
                                    transformOrigin: 'center',
                                    transition: 'transform 0.2s'
                                }}
                            />
                        </Box>
                    ) : isImage && previewUrl ? (
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                overflow: 'hidden'
                            }}
                        >
                            <Box
                                component="img"
                                src={previewUrl}
                                alt={file.originalFileName}
                                sx={{
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    transform: `scale(${scale}) rotate(${rotation}deg)`,
                                    transition: 'transform 0.2s'
                                }}
                                onError={() => {
                                    setError('Failed to load image preview');
                                }}
                            />
                        </Box>
                    ) : (
                        <Alert severity="info" sx={{ width: '100%' }}>
                            Preview is not available for this file type. Please download the file to view it.
                        </Alert>
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <Box display="flex" justifyContent="space-between" width="100%" px={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                        {(isPDF || isImage) && (
                            <>
                                <IconButton onClick={zoomOut} disabled={scale <= 0.5}>
                                    <ZoomOutIcon />
                                </IconButton>
                                <Typography variant="body2">
                                    {Math.round(scale * 100)}%
                                </Typography>
                                <IconButton onClick={zoomIn} disabled={scale >= 3.0}>
                                    <ZoomInIcon />
                                </IconButton>
                                <IconButton onClick={rotate}>
                                    <RotateIcon />
                                </IconButton>
                                {isPDF && (
                                    <IconButton onClick={handleOpenInNewTab} title="Open in new tab">
                                        <OpenInNewIcon />
                                    </IconButton>
                                )}
                            </>
                        )}
                    </Box>
                    
                    <Button onClick={onClose}>
                        Close
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export { FilePreviewModal };